import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";
import { Field, LoginBadge, ROLE_LABEL, RoleBadge, StatusBadge, fmtDate } from "./accountShared";

const TABS = ["overview", "profile", "security", "sessions", "activity", "audit"];

const Overview = ({ d }) => (
  <div className="grid sm:grid-cols-2 gap-4" data-testid="account-overview">
    <Field label="Name" testId="ov-name">{d.name}</Field>
    <Field label="Email" testId="ov-email">{d.email}</Field>
    <Field label="Role"><RoleBadge role={d.role} testId="ov-role" /></Field>
    <Field label="Account status"><StatusBadge status={d.account_status} testId="ov-status" /></Field>
    <Field label="Created" testId="ov-created">{fmtDate(d.created_at)}</Field>
    <Field label="Last login" testId="ov-last-login">{fmtDate(d.last_login_at)}</Field>
    <Field label="Last activity" testId="ov-last-activity">{fmtDate(d.last_activity_at)}</Field>
    <Field label="Verification" testId="ov-verified">{d.email_verified ? "Email verified" : "Email not verified"}{d.security?.auth_provider === "google" ? " · Google sign-in" : ""}</Field>
    {d.company_name && <Field label="Company" testId="ov-company">{d.company_name}</Field>}
    {d.phone && <Field label="Mobile" testId="ov-phone">{d.phone}</Field>}
    {d.partner && <Field label="Partner approval" testId="ov-partner">{d.partner.partner_status}{d.partner.reviewed_by ? ` · reviewed by ${d.partner.reviewed_by}` : ""}{d.partner.review_note ? ` · "${d.partner.review_note}"` : ""} · {d.partner.machines} machine(s) · {d.partner.orders} order(s)</Field>}
    {d.customer && <Field label="Customer records" testId="ov-customer">{d.customer.rfqs} RFQ(s) · {d.customer.orders} order(s) · {d.customer.files} file(s)</Field>}
  </div>
);

const Profile = ({ d, onSaved }) => {
  const [f, setF] = useState({ name: d.name || "", email: d.email || "", phone: d.phone || "", company_name: d.company_name || "", city: d.city || "", state: d.state || "", address: d.address || "", gst_number: d.gst_number || "", processes: (d.processes || []).join(", "), email_verified: !!d.email_verified });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, processes: f.processes.split(",").map((s) => s.trim()).filter(Boolean) };
      if (d.role !== "partner") { delete payload.city; delete payload.state; delete payload.address; delete payload.gst_number; delete payload.processes; }
      const { data } = await adminApi.patch(`/admin/accounts/${d.id}`, payload);
      toast.success("Profile saved.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="account-profile-form">
      <div className="space-y-1"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" /></div>
      <div className="space-y-1"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="profile-email" /></div>
      <div className="space-y-1"><Label>Mobile</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" /></div>
      <div className="space-y-1"><Label>Company</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="profile-company" /></div>
      {d.role === "partner" && <>
        <div className="space-y-1"><Label>City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" /></div>
        <div className="space-y-1"><Label>State</Label><Input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="profile-state" /></div>
        <div className="space-y-1 sm:col-span-2"><Label>Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="profile-address" /></div>
        <div className="space-y-1"><Label>GST number</Label><Input value={f.gst_number} onChange={(e) => set("gst_number", e.target.value)} data-testid="profile-gst" /></div>
        <div className="space-y-1"><Label>Processes</Label><Input value={f.processes} onChange={(e) => set("processes", e.target.value)} data-testid="profile-processes" /></div>
      </>}
      <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 sm:col-span-2"><Label className="text-sm">Email verified</Label><Switch checked={f.email_verified} onCheckedChange={(v) => set("email_verified", v)} data-testid="profile-verified" /></div>
      <p className="text-xs text-muted-foreground sm:col-span-2">Passwords, tokens and keys are never shown or editable here. Changes are audited.</p>
      <div className="sm:col-span-2 flex justify-end"><Button type="submit" disabled={busy} data-testid="profile-save-btn">{busy ? "Saving…" : "Save profile"}</Button></div>
    </form>
  );
};

const Security = ({ d, onAction, isSelf }) => {
  const s = d.security || {};
  const [role, setRole] = useState(d.role);
  return (
    <div className="space-y-5" data-testid="account-security">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email verified" testId="sec-verified">{s.email_verified ? "Yes" : "No"}</Field>
        <Field label="Account status"><StatusBadge status={s.account_status} testId="sec-status" /></Field>
        <Field label="Failed login attempts" testId="sec-failed">{s.failed_attempts ?? 0}</Field>
        <Field label="Lock status" testId="sec-locked">{s.locked ? `Locked until ${fmtDate(s.locked_until)}` : "Unlocked"}</Field>
        <Field label="Password" testId="sec-password">{s.auth_provider === "google" ? "Google sign-in" : s.has_password ? "Set" : "Not set (invitation pending)"}</Field>
        <Field label="Password reset" testId="sec-reset">{s.password_reset_pending ? `Link pending · expires ${fmtDate(s.password_reset_pending.expires_at)}` : "No pending link"}</Field>
        <Field label="MFA / 2FA" testId="sec-mfa">Not available in this system</Field>
        <Field label="Session revocations" testId="sec-revocations">{d.sessions?.revocations ?? 0}</Field>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="security-actions">
        <Button size="sm" variant="outline" onClick={() => onAction("reset-password", d)} data-testid="sec-reset-btn">{s.has_password ? "Reset password" : "Send invitation link"}</Button>
        {s.locked && <Button size="sm" variant="outline" onClick={() => onAction("unlock", d)} data-testid="sec-unlock-btn">Unlock account</Button>}
        {!isSelf && s.account_status !== "active" && <Button size="sm" variant="outline" onClick={() => onAction("activate", d)} data-testid="sec-activate-btn">Activate</Button>}
        {!isSelf && s.account_status === "active" && <Button size="sm" variant="outline" onClick={() => onAction("suspend", d)} data-testid="sec-suspend-btn">Suspend</Button>}
        {!isSelf && s.account_status !== "disabled" && <Button size="sm" variant="destructive" onClick={() => onAction("disable", d)} data-testid="sec-disable-btn">Disable</Button>}
      </div>
      {!isSelf && (
        <div className="rounded-xl border border-border p-4 space-y-3" data-testid="role-management">
          <p className="text-sm font-medium">Role management</p>
          <p className="text-xs text-muted-foreground">Current role: <RoleBadge role={d.role} testId="role-current" />. Changing a role requires typed confirmation, is backend-validated and audited, and signs the user out everywhere.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-48" data-testid="role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`role-option-${k}`}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={role === d.role} onClick={() => onAction("role", d, { role })} data-testid="role-change-btn">Change role…</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Sessions = ({ d, onAction }) => (
  <div className="space-y-4" data-testid="account-sessions">
    <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="sessions-unavailable">No Active Session Data Available — this system issues stateless tokens and does not track devices, browsers or IPs per session. Login history below comes from the audit log.</div>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Last login" testId="sess-last-login">{fmtDate(d.last_login_at)}</Field>
      <Field label="Last activity" testId="sess-last-activity">{fmtDate(d.last_activity_at)}</Field>
      <Field label="Login status"><LoginBadge status={d.login_status} testId="sess-login-status" /></Field>
      <Field label="Sessions revoked so far" testId="sess-revocations">{d.sessions?.revocations ?? 0}</Field>
    </div>
    <Button size="sm" variant="destructive" onClick={() => onAction("force-logout", d)} data-testid="sess-logout-all-btn">Force logout · all sessions</Button>
  </div>
);

const Events = ({ id, view }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    adminApi.get(`/admin/accounts/${id}/events`, { params: { view } }).then((r) => setRows(r.data.items)).catch(() => setRows([]));
  }, [id, view]);
  if (!rows) return <div className="py-8 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6" data-testid={`${view}-empty`}>No {view} records for this account yet.</p>;
  return (
    <div className="space-y-1.5" data-testid={`account-${view}`}>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-xs" data-testid={`${view}-row-${r.id}`}>
          <Badge variant="secondary" className="font-mono-tech">{r.action}</Badge>
          <Badge variant="outline" className={r.result === "denied" ? "text-red-600 dark:text-red-400" : ""}>{r.result}</Badge>
          <span className="text-muted-foreground">{r.direction === "target" ? `by ${r.actor_email || "system"}` : r.actor_email}</span>
          {r.ip && <span className="text-muted-foreground">· {r.ip}</span>}
          {r.meta?.target_email && r.direction === "actor" && <span className="text-muted-foreground">→ {r.meta.target_email}</span>}
          {r.meta?.reason && <span className="text-muted-foreground">· {r.meta.reason}</span>}
          {r.meta?.to && <span className="text-muted-foreground">· {r.meta.from} → {r.meta.to}</span>}
          <span className="ml-auto text-muted-foreground">{fmtDate(r.ts)}</span>
        </div>
      ))}
    </div>
  );
};

export const AccountDetailSheet = ({ userId, tab, onTab, onClose, onAction, refreshKey, selfId }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!userId) return;
    setD(null);
    adminApi.get(`/admin/accounts/${userId}`).then((r) => setD(r.data)).catch((err) => { toast.error(formatApiErrorDetail(err.response?.data?.detail)); onClose(); });
  }, [userId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="account-detail-sheet">
        {!d ? <div className="py-16 flex justify-center" data-testid="account-detail-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="flex flex-wrap items-center gap-2" data-testid="account-detail-title">{d.name || d.email}<RoleBadge role={d.role} /><StatusBadge status={d.account_status} testId="account-detail-status" /></SheetTitle>
              <SheetDescription data-testid="account-detail-email">{d.email}</SheetDescription>
            </SheetHeader>
            <Tabs value={TABS.includes(tab) ? tab : "overview"} onValueChange={onTab} className="mt-4">
              <TabsList className="w-full justify-start overflow-x-auto h-auto" data-testid="account-detail-tabs">{TABS.map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`account-tab-${t}`}>{t}</TabsTrigger>)}</TabsList>
              <TabsContent value="overview" className="mt-4"><Overview d={d} /></TabsContent>
              <TabsContent value="profile" className="mt-4"><Profile key={d.updated_at} d={d} onSaved={(u) => { setD({ ...d, ...u }); onAction("refresh", u); }} /></TabsContent>
              <TabsContent value="security" className="mt-4"><Security d={d} onAction={onAction} isSelf={d.id === selfId} /></TabsContent>
              <TabsContent value="sessions" className="mt-4"><Sessions d={d} onAction={onAction} /></TabsContent>
              <TabsContent value="activity" className="mt-4"><Events id={d.id} view="activity" /></TabsContent>
              <TabsContent value="audit" className="mt-4"><Events id={d.id} view="audit" /></TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
